<template>
  <div>
    <nav>
      <ul class="pager">
        <li class="previous">
          <router-link :to="{ name: 'client_dashboard', client_id: $route.params.client_id }">
            <span aria-hidden="true">&larr;</span> Client dashboard
          </router-link>
        </li>
      </ul>
    </nav>

    <div v-if="fetching">
      <h1>Winter Consult Summary Report</h1>
      <h4>This may take a few minutes</h4>
      <loader></loader>
    </div>

    <div v-else>
      <div class="sections">
        <h1>Key Vet Winter Consult {{ model.Misc.season_start_year }} Summary Report</h1>
        <br />
        <div class="form-group">
          <h2 class="text-left alert alert-success">Introduction</h2>
          <textarea
            rows="5"
            cols="50"
            v-model="model['keypoints']"
            class="form-control"
            placeholder="Write a Key Points Summary"
          >
          </textarea>
        </div>

        <client-details :model="model['ClientDetails']" @onRemoveSection="removeSection"> </client-details>
        <hr />

        <farm-details :model="model['FarmDetails']" @onRemoveSection="removeSection"> </farm-details>
        <hr />

        <animal-numbers :model="model['AnimalNumbers']" @onRemoveSection="removeSection"> </animal-numbers>
        <hr />

        <historic-production :model="model['HistoricProduction']" @onRemoveSection="removeSection">
        </historic-production>
        <hr />

        <mastitis-milk-quality
          :model="model['MastitisMilkQuality']"
          @onRemoveSection="removeSection"
          ref="mmq_component"
        >
        </mastitis-milk-quality>
        <hr />

        <reproduction-planning
          :model="model['ReproductionPlanning']"
          @onRemoveSection="removeSection"
          ref="repro_planning_component"
        >
        </reproduction-planning>
        <hr />

        <trace-elements :model="model['TraceElements']" @onRemoveSection="removeSection" ref="trace_element_component">
        </trace-elements>
        <hr />

        <welfare-monitoring :model="model['WelfareMonitoring']" @onRemoveSection="removeSection"> </welfare-monitoring>
        <hr />

        <animal-health :model="model['AnimalHealth']" @onRemoveSection="removeSection"> </animal-health>
        <hr />

        <preventive-health-and-vacination
          :model="model['PreventiveHealthAndVacination']"
          @onRemoveSection="removeSection"
        >
        </preventive-health-and-vacination>
        <hr />

        <young-stock :model="model['YoungStock']" @onRemoveSection="removeSection"> </young-stock>
        <hr />

        <drug-allocations :model="model['DrugAllocations']" @onRemoveSection="removeSection"> </drug-allocations>
        <hr />

        <div class="form-group">
          <h2 class="text-left alert alert-success">Final comments</h2>
          <textarea rows="5" cols="50" v-model="model.Misc.summary_final_comment" class="form-control"> </textarea>
        </div>

        <button v-if="!generatingPDF" class="btn btn-success" v-on:click="generateReport">Generate PDF</button>
        <loader v-if="generatingPDF"></loader>
      </div>
    </div>
  </div>
</template>

<script>
import { WCSummaryComponents } from '@/components/reports/wc_summary'
import { BASE_API_URL, consultFormService, reportService } from '@/_services'
import { authHeader, handleResponse } from '@/_helpers'
import axios from 'axios'

export default {
  name: 'WinterConsultSummaryPage',
  components: {
    ...WCSummaryComponents,
  },
  data() {
    return {
      model: {},
      clients: [],
      selected: {
        display_name: '',
      },
      search_type: 'name',
      fetching: true,
      generatingPDF: false,
      removedSections: {
        ClientDetails: { removed: false },
        FarmDetails: { removed: false },
        AnimalNumbers: { removed: false },
        HistoricProduction: { removed: false },
        MastitisMilkQuality: { removed: false },
        ReproductionPlanning: { removed: false },
        TraceElements: { removed: false },
        WelfareMonitoring: { removed: false },
        AnimalHealth: { removed: false },
        PreventiveHealthAndVaccination: { removed: false },
        YoungStock: { removed: false },
        DrugAllocations: { removed: false },
      },
      finalComment: '',
    }
  },
  mounted: function () {
    this.clientId = this.$route.params.client_id
    this.season = this.$route.params.season
    this.onSubmit()
  },
  methods: {
    generateReport: function () {
      if (!this.removedSections['TraceElements'].removed) {
        this.model['TraceElements']['CHARTS_b64'] = {}
        if (this.$refs.trace_element_component.$refs.serum_mg_chart) {
          this.model['TraceElements']['CHARTS_b64']['serum_mg'] =
            this.$refs.trace_element_component.$refs.serum_mg_chart.$refs.canvas.toDataURL()
        }
        if (this.$refs.trace_element_component.$refs.serum_se_chart) {
          this.model['TraceElements']['CHARTS_b64']['serum_se'] =
            this.$refs.trace_element_component.$refs.serum_se_chart.$refs.canvas.toDataURL()
        }
        if (this.$refs.trace_element_component.$refs.liver_cu_chart) {
          this.model['TraceElements']['CHARTS_b64']['liver_cu'] =
            this.$refs.trace_element_component.$refs.liver_cu_chart.$refs.canvas.toDataURL()
        }
      }
      if (!this.removedSections['MastitisMilkQuality'].removed) {
        this.model['MastitisMilkQuality']['CHARTS_b64'] = {}

        for (var farm of this.model['MastitisMilkQuality'].FARMS) {
          let key = 'farm_' + farm.id + '-mastitis_treatments_chart'

          if (this.$refs.mmq_component.$refs[key][0]) {
            this.model['MastitisMilkQuality']['CHARTS_b64'][key] =
              this.$refs.mmq_component.$refs[key][0].$refs.canvas.toDataURL()
          }
        }
      }
      if (!this.removedSections['ReproductionPlanning'].removed) {
        this.model['ReproductionPlanning']['CHARTS_b64'] = {}
        if (this.$refs.repro_planning_component.$refs.metricheck_clean) {
          this.model['ReproductionPlanning']['CHARTS_b64']['metricheck_clean'] =
            this.$refs.repro_planning_component.$refs.metricheck_clean.$refs.canvas.toDataURL()
        }
        if (this.$refs.repro_planning_component.$refs.cuemate_cidr) {
          this.model['ReproductionPlanning']['CHARTS_b64']['cuemate_cidr'] =
            this.$refs.repro_planning_component.$refs.cuemate_cidr.$refs.canvas.toDataURL()
        }
        if (this.$refs.repro_planning_component.$refs.non_cycling_exam) {
          this.model['ReproductionPlanning']['CHARTS_b64']['non_cycling_exam'] =
            this.$refs.repro_planning_component.$refs.non_cycling_exam.$refs.canvas.toDataURL()
        }
      }

      this.generatingPDF = true

      return axios({
        method: 'POST',
        url: `${BASE_API_URL}/reports/wc_summary/${this.clientId}/${this.season}/export_pdf`,
        headers: authHeader(),
        responseType: 'blob',
        data: {
          model: this.model,
          configuration: this.removedSections,
        },
      })
        .catch(handleResponse)
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')
          link.href = url
          let reportName = `${this.model.ClientDetails.client_code}_winter_consult_summary-${this.season}.pdf`
          link.setAttribute('download', reportName) //or any other extension
          document.body.appendChild(link)
          link.click()
          link.remove()
          this.generatingPDF = false
        })
    },
    onSubmit: function () {
      this.fetching = true

      // if (this.selected.id) {
      let clientId = this.$route.params.client_id
      let season = this.$route.params.season

      // TODO: load data and settings in same call.
      consultFormService
        .getSummaryReportSettings({ clientId: this.clientId, consultType: 'winter_consult' })
        .then((data) => {
          this.removedSections = data.settings
        })
      // TODO: refactor all summary reports to work the same
      reportService
        .getWinterConsultSummaryReportData(clientId, season)
        .then((data) => {
          this.model = data
          this.fetching = false
        })
        .catch(() => {
          this.fetching = false
          this.$store.dispatch('alert/error', {
            message: 'An error occurred. Contact the administrator.',
            persist: true,
          })
          this.$router.push('/')
        })
    },
    removeSection: function (section, subsection) {
      if (subsection) {
        if (this.removedSections[section][subsection] == undefined) {
          this.$set(this.removedSections[section], subsection, true)
        } else {
          this.$set(this.removedSections[section], subsection, !this.removedSections[section][subsection])
        }
      } else {
        this.removedSections[section]['removed'] = !this.removedSections[section]['removed'] || false
      }
    },
  },
}
</script>

<style>
dd {
  text-align: left;
}
.sections {
  padding-left: 20px;
}
.farm-fields {
  float: left;
  width: 350px;
  margin-right: 3em;
}
.glyphicon-remove {
  color: red;
}
.glyphicon-plus {
  color: #5cb85c;
}
.dl-horizontal dt {
  white-space: normal;
}
.subsection-disabled {
  text-decoration: line-through;
}
</style>
